import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Button } from "@/components/flexkit/Button";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Subheading } from "@/components/podkit/typography/Headings";
import { useDocumentTitle } from "@/hooks/use-document-title";
import {
    KNOWN_EXPERIMENTS,
    useExperimentsEnabled,
    useExperimentVisibleInSettings,
    useAnyExperimentVisibleInSettings,
    type ExperimentName,
} from "@/hooks/use-experiment";
import { useSegmentTrack } from "@/hooks/use-segment";
import { IconTestTube } from "central-icons/IconTestTube";
import { FlaskConical } from "lucide-react";
import type { FC } from "react";

type ExperimentsSettingsPageProps = {
    hideDescription?: boolean;
};

export const ExperimentsSettingsPage: FC<ExperimentsSettingsPageProps> = ({ hideDescription }) => {
    useDocumentTitle("Experiments");

    const { enabled: experimentsEnabled, setEnabled: setExperimentsEnabled, loading } = useExperimentsEnabled();
    const { visible: anyVisible, loading: visibilityLoading } = useAnyExperimentVisibleInSettings();
    const segmentTrack = useSegmentTrack();

    if (loading || visibilityLoading) {
        return <LoadingState />;
    }

    // ConfigCat targeting rules determine visibility per org/tier/user.
    // If no experiments are visible for this user, they shouldn't see this page.
    if (!anyVisible) {
        return <NotAuthorized />;
    }

    // Opted out — show re-enable prompt
    if (!experimentsEnabled) {
        return (
            <div className="flex flex-col gap-8" data-testid="experiments-settings-page">
                {!hideDescription && <ExperimentsHeader />}
                <div className="flex flex-col items-center gap-4 rounded-xl border border-border-base bg-surface-secondary p-8 text-center">
                    <IconTestTube size={40} />
                    <div className="space-y-1">
                        <div className="font-medium text-content-primary">Experiments are disabled</div>
                        <div className="text-sm text-content-secondary">
                            You&apos;ve opted out of experimental features. Re-enable to try new features before
                            they&apos;re generally available.
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setExperimentsEnabled(true);
                            segmentTrack("experiments_global_enabled", { source: "settings" });
                            toast({ title: "Experiments enabled!" });
                        }}
                        data-tracking-id="enable-experiments-global"
                    >
                        Re-enable experiments
                    </Button>
                </div>
            </div>
        );
    }

    // Default — show experiments with global opt-out
    return (
        <div className="flex flex-col gap-8" data-testid="experiments-settings-page">
            {!hideDescription && <ExperimentsHeader />}

            <div className="flex flex-col gap-4">
                {Object.keys(KNOWN_EXPERIMENTS).map((name) => (
                    <ExperimentCard key={name} experimentName={name as ExperimentName} />
                ))}
            </div>

            <div>
                <button
                    className="text-sm text-content-secondary underline hover:text-content-primary"
                    onClick={() => {
                        setExperimentsEnabled(false);
                        segmentTrack("experiments_global_disabled", { source: "settings" });
                        toast({ title: "Experiments disabled" });
                    }}
                    data-tracking-id="disable-experiments-global"
                >
                    Disable all experiments
                </button>
            </div>
        </div>
    );
};

const ExperimentsHeader: FC = () => (
    <div className="space-y-1">
        <div className="flex items-center gap-2">
            <FlaskConical className="size-5 text-content-secondary" />
            <Heading3>Experiments</Heading3>
        </div>
        <Subheading>
            Experimental features let you try new functionality before it&apos;s generally available. These features may
            change or be removed at any time.
        </Subheading>
    </div>
);

type ExperimentCardProps = {
    experimentName: ExperimentName;
};

const ExperimentCard: FC<ExperimentCardProps> = ({ experimentName }) => {
    const experiment = KNOWN_EXPERIMENTS[experimentName];
    const { visible } = useExperimentVisibleInSettings(experimentName);

    // Don't render experiments that aren't visible for this user's targeting
    if (!visible) {
        return null;
    }

    return (
        <div className="flex items-center rounded-xl border border-border-base bg-surface-secondary p-4">
            <div className="flex items-center gap-3">
                {experimentName === "ralph" ? (
                    <IconRalphWiggum size={32} className="shrink-0" />
                ) : (
                    <FlaskConical className="size-8 shrink-0 text-content-secondary" />
                )}
                <div>
                    <div className="font-medium text-content-primary">{experiment.name}</div>
                    <div className="text-sm text-content-secondary">{experiment.description}</div>
                </div>
            </div>
        </div>
    );
};
